<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      v-if="isAdmin"
      :icon="AddIcon"
      :label="$t('pages.mountings.mountingList.modal.new.buttonText')"
      @click="mountingModalOpen = true"
    />
    <export
      url="/mountings/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t(`csv.title.mountings.${variant}`)"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <mounting-modal
    v-model:open="mountingModalOpen"
    v-model:mounting="editedMounting"
    @saved="refetch"
  />
  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :row-link="
          (item) => ({ name: 'mounting-timeslots-active', params: { mountingId: item.id } })
        "
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :actions="isAdmin ? actions : undefined"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {collOptionsProvider, ProviderParams, useProvider, withExtraOptions} from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Company, HydraCollection, License, Mounting, Region } from '@/entities/Entities';

import { useDelete } from '@hooks/api';
import MountingModal from '@pages/mountings/MountingModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useStore } from '@/store';
import { useQueryRef } from '@/hooks/url';
import { format } from 'date-fns';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  variant: 'active' | 'passive' | 'archived';
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'updatedAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const mountingModalOpen = ref(false);
const editedMounting = ref();

const [deleteMounting, { loading: deleting }] = useDelete();

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];

const filter = useQueryRef('filter', {});

const fixedFilter = computed(() => ({
  state: props.variant,
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const actions = [
  {
    title: t('pages.mountings.mountingList.actions.edit'),
    action: (item: Mounting) => {
      mountingModalOpen.value = true;
      editedMounting.value = { ...item, region: item.region?.['@id'] };
    },
    condition: (item: any) => isAdmin,
  },
  {
    title: t('pages.mountings.mountingList.actions.delete'),
    action: async (item: Mounting) => {
      if (await deleteMounting(`/mountings/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
    condition: (item: any) => isAdmin,
  },
];

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.mountings.mountingList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'region.name',
    title: t('mounting.region'),
    group: t('pages.mountings.mountingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (value: string) => value ?? t('mounting.home'),
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.mountings.mountingList.filterGroups.general'),
      options: {
        options: withExtraOptions(collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })), [
          {
            label: t('mounting.home'),
            value: 'home',
          },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'home') {
            delete m['region'];
            m['exists[region]'] = false;
          } else {
            delete m['exists[region]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[region]' in m) {
            return 'home';
          }
          return m[key];
        },
        label: t('mounting.region'),
      },
    },
  },
  {
    key: 'address',
    title: t('mounting.address'),
    group: t('pages.mountings.mountingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.mountings.mountingList.filterGroups.general'),
      options: {
        label: t('mounting.address'),
      },
    },
  },
  {
    key: 'company.name',
    title: t('licenses.company'),
    group: t('pages.mountings.mountingList.fieldGroups.general'),
    sortable: true,
    to: (mounting: Mounting) =>
      (mounting?.company?.id
        ? {
          name: 'licenses-company-details',
          params: { companyId: mounting?.company?.id },
        }
        : undefined),
    filter: {
      key: 'company',
      type: 'dropdown',
      group: t('pages.mountings.mountingList.fieldGroups.general'),
      options: {
        label: t('licenses.company'),
        searchable: true,
        filterResults: false,
        clearOnSelect: true,
        delay: 0,
        resolveOnLoad: true,
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          const extraOptions = [
            {
              label: t('licenses.noCompany'),
              value: 'none',
              disabled: false,
            },
            {
              label: t('licenses.anyCompany'),
              value: 'any',
              disabled: false,
            },
          ];

          if (!input || input === '') {
            extraOptions.push({
              label: t('inputSearchString'),
              value: '-1',
              disabled: true,
            });
            return Promise.resolve(extraOptions);
          }

          const filterQuery: ProviderParams = {};
          filterQuery.filter = {};
          if(query) {
            filterQuery.filter.name = input;
          } else if (!['any', 'none'].includes(select.modelValue)) {
            filterQuery.filter.id = select.modelValue;
          }

          return withExtraOptions(collOptionsProvider<Company>('/companies', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          })),extraOptions)();
        },
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'none') {
            delete m['company'];
            m['exists[company]'] = false;
          } else if (value === 'any') {
            delete m['company'];
            m['exists[company]'] = true;
          } else {
            delete m['exists[company]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[company]' in m) {
            return m['exists[company]'] ? 'any' : 'none';
          }
          return m[key];
        },
      },
    },
  },
  {
    key: 'license',
    title: t('licenses.license'),
    group: t('pages.mountings.mountingList.fieldGroups.general'),
    sortable: true,
    sortKey: 'license.company.name',
    formatter: (value) => {
      if (!value) return t('licenses.no');
      return `${value.company?.name} (${value.startDate ? format(new Date(value.startDate), 'dd.MM.yyyy') : ''} - ${
        value.endDate ? format(new Date(value.endDate), 'dd.MM.yyyy') : ''
      })`;
    },
    filter: {
      key: 'license',
      type: 'dropdown',
      group: t('pages.mountings.mountingList.fieldGroups.general'),
      options: {
        label: t('licenses.license'),
        searchable: true,
        filterResults: false,
        clearOnSelect: true,
        delay: 0,
        resolveOnLoad: true,
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          const extraOptions = [
            {
              label: t('licenses.no'),
              value: 'none',
              disabled: false,
            },
            {
              label: t('licenses.any'),
              value: 'any',
              disabled: false,
            },
          ];

          if (!input || input === '') {
            extraOptions.push({
              label: t('inputSearchString'),
              value: '-1',
              disabled: true,
            });
            return Promise.resolve(extraOptions);
          }

          const filterQuery: ProviderParams = {};
          filterQuery.filter = {};
          if(query) {
            filterQuery.filter['company.name'] = input;
          } else if (!['any', 'none'].includes(select.modelValue)) {
            filterQuery.filter.id = select.modelValue;
          }

          return withExtraOptions(collOptionsProvider<License>('/licenses', filterQuery, (value) => ({
            label: `${value.company?.name} (${value.startDate ? format(new Date(value.startDate), 'dd.MM.yyyy') : ''} - ${value.endDate ? format(new Date(value.endDate), 'dd.MM.yyyy') : ''})`,
            value: value.id,
          })),extraOptions)();
        },
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'none') {
            delete m['license'];
            m['exists[license]'] = false;
          } else if (value === 'any') {
            delete m['license'];
            m['exists[license]'] = true;
          } else {
            delete m['exists[license]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[license]' in m) {
            return m['exists[license]'] ? 'any' : 'none';
          }
          return m[key];
        },
      },
    },
  },
  {
    key: 'totalBookingNumbers',
    title: t('mounting.totalBookingNumbers'),
    group: t('pages.mountings.mountingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (value: { totalBooked: string; totalSlots: string|null }) =>
      `${value.totalBooked} / ${value.totalSlots ?? 0}`,
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    group: t('pages.mountings.mountingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.mountings.mountingList.filterGroups.general'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<Mounting>>('/mountings', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

